import { useEffect } from 'react';

/**
 * Global error capture for the in-app feedback widget. Mounted once at the app
 * root; buffers the last 15 errors (window errors, unhandled promise
 * rejections, console.error calls) in `window.__errorLogs` so the feedback
 * modal can attach them to a bug report. Additive to Sentry — it never
 * swallows anything, and restores console.error on unmount.
 */

export interface CapturedError {
  message: string;
  stack?: string;
  type: 'error' | 'unhandledrejection' | 'console.error';
  timestamp: string;
}

declare global {
  interface Window {
    __errorLogs?: CapturedError[];
  }
}

const MAX_LOGS = 15;

function pushLog(entry: CapturedError) {
  if (!window.__errorLogs) window.__errorLogs = [];
  window.__errorLogs.push(entry);
  if (window.__errorLogs.length > MAX_LOGS) {
    window.__errorLogs.splice(0, window.__errorLogs.length - MAX_LOGS);
  }
}

export function ErrorListener() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      pushLog({
        message: e.message || 'Unknown error',
        stack: e.error?.stack,
        type: 'error',
        timestamp: new Date().toISOString(),
      });
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const reason: unknown = e.reason;
      pushLog({
        message:
          reason instanceof Error
            ? reason.message
            : typeof reason === 'string'
              ? reason
              : (() => { try { return JSON.stringify(reason); } catch { return String(reason); } })(),
        stack: reason instanceof Error ? reason.stack : undefined,
        type: 'unhandledrejection',
        timestamp: new Date().toISOString(),
      });
    };

    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      try {
        pushLog({
          message: args
            .map((a) => {
              if (a instanceof Error) return a.message;
              if (typeof a === 'string') return a;
              try { return JSON.stringify(a); } catch { return String(a); }
            })
            .join(' ')
            .slice(0, 500),
          stack: args.find((a): a is Error => a instanceof Error)?.stack,
          type: 'console.error',
          timestamp: new Date().toISOString(),
        });
      } catch {
        // Never let the capture break the original logging.
      }
      originalConsoleError.apply(console, args as Parameters<typeof console.error>);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      console.error = originalConsoleError;
    };
  }, []);

  return null;
}
